import ButtonMUI from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {
  BORDER_RADIUS_BUTTON,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
  HEIGHT_BUTTON,
} from "../../utils/constants";
import {
  BOX_SHADOW_BUTTON,
  INPUT_COLOR_BORDER,
  PRIMARY_COLOR_TEXT,
} from "../../utils/colors";
import { Tooltip, useTheme } from "@mui/material";

const sxButtonPrimary = (theme) => ({
  background: theme.palette.button.primary.main,
  color: theme.palette.button.primary.contrastText,
  fontSize: FONT_SIZE_BUTTON,
  boxShadow: BOX_SHADOW_BUTTON,
  "&:hover": {
    background: `${theme.palette.button.primary.hover.main} !important`,
    color: `${theme.palette.button.primary.hover.contrastText} !important`,
  },
});

const sxButtonSecondary = (theme) => ({
  backgroundColor: theme.palette.button.secondary.main,
  color: PRIMARY_COLOR_TEXT,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: BOX_SHADOW_BUTTON,
  fontSize: FONT_SIZE_BUTTON,
  "&:hover": {
    backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
    color: `${PRIMARY_COLOR_TEXT} !important`,
  },
});

const sxButtonSecondaryIconOnly = (theme) => ({
  backgroundColor: theme.palette.button.secondary.main,
  color: PRIMARY_COLOR_TEXT,
  border: "none",
  boxShadow: BOX_SHADOW_BUTTON,
  fontSize: FONT_SIZE_BUTTON,
  "&:hover": {
    backgroundColor: `${theme.palette.button.secondary.main} !important`,
    color: `${PRIMARY_COLOR_TEXT} !important`,
    boxShadow: BOX_SHADOW_BUTTON,
  },
});

const sxButtonPrimaryDisabled = {
  background: "linear-gradient(135deg, #2e2e2e 0%, #1f1f1f 100%) !important",
  filter: "contrast(0.6) !important",
  color: "#9ca3af",
};

const sxButtonSecondaryDisabled = {
  backgroundColor: "rgba(255, 255, 255, 0.8) !important",
  color: "rgba(0, 0, 0, 0.4) !important",
};

const Button = ({
  id,
  children,
  variant = "contained",
  component = "button",
  color = "primary",
  fullWidth = false,
  disableElevation = true,
  disabled = false,
  size = "medium",
  ariaLabel = "",
  type = "",
  startIcon = "",
  endIcon = "",
  sx = {},
  onClick,
  dataTour = "",
  refButton,
  loading = false,
  tooltip = "",
  form = undefined,
  iconOnly = false,
}) => {
  const theme = useTheme();
  const sxBase = {
    "&.Mui-disabled": {
      ...(color === "secondary" && sxButtonSecondaryDisabled),
      ...(color === "primary" && sxButtonPrimaryDisabled),
    },
    ...(color === "primary" && sxButtonPrimary(theme)),
    ...(color === "secondary" &&
      (iconOnly ? sxButtonSecondaryIconOnly(theme) : sxButtonSecondary(theme))),
    borderRadius: BORDER_RADIUS_BUTTON,
    fontWeight: FONT_WEIGHT_BUTTON,
    height: HEIGHT_BUTTON,
    minWidth: iconOnly ? 48 : 130,
    ...(iconOnly && {
      width: 48,
      px: 0,
      "& .MuiButton-startIcon": {
        margin: 0,
      },
    }),
    lineHeight: "normal",
    py: iconOnly ? 0 : "10px",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
    fontSize: `${FONT_SIZE_BUTTON}px !important`,
    ...sx,
    "&:hover": {
      ...(sx?.["&:hover"] || {}),
      ...((color === "primary" && sxButtonPrimary(theme)?.["&:hover"]) || {}),
      ...((color === "secondary" &&
        !iconOnly &&
        sxButtonSecondary(theme)?.["&:hover"]) ||
        {}),
      ...((color === "secondary" &&
        iconOnly &&
        sxButtonSecondaryIconOnly(theme)?.["&:hover"]) ||
        {}),
    },
  };

  if (tooltip) {
    // To ensure the tooltip appears even when the button is disabled,
    // wrap the ButtonMUI in a span, since disabled elements do not trigger tooltips.
    return (
      <Tooltip title={tooltip}>
        <span style={{ display: "inline-block" }}>
          <ButtonMUI
            id={id}
            ref={refButton}
            data-tour={dataTour}
            form={form}
            variant={variant}
            disabled={loading || disabled}
            color={color}
            component={component}
            fullWidth={fullWidth}
            disableElevation={disableElevation}
            aria-label={ariaLabel}
            size={size}
            type={type}
            startIcon={loading && iconOnly ? undefined : startIcon}
            endIcon={endIcon}
            sx={sxBase}
            onClick={onClick}
          >
            {loading ? (
              <CircularProgress
                size={24}
                color={color === "primary" ? "inherit" : "primary"}
              />
            ) : (
              children
            )}
          </ButtonMUI>
        </span>
      </Tooltip>
    );
  }

  return (
    <ButtonMUI
      id={id}
      ref={refButton}
      data-tour={dataTour}
      variant={variant}
      disabled={loading || disabled}
      color={color}
      component={component}
      fullWidth={fullWidth}
      disableElevation={disableElevation}
      aria-label={ariaLabel}
      size={size}
      type={type}
      startIcon={loading && iconOnly ? undefined : startIcon}
      endIcon={endIcon}
      sx={sxBase}
      onClick={onClick}
      form={form}
    >
      {loading ? (
        <CircularProgress
          size={24}
          color={color === "primary" ? "inherit" : "primary"}
        />
      ) : (
        children
      )}
    </ButtonMUI>
  );
};

export default Button;
